import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import NavigationStackStore from './NavigationStackStore';
import { routeLabels } from './RouteLabelResolver';
import { previousEntry, reduceHistory } from './historyStack';

const AppHistoryContext = createContext({ canGoBack: false, previous: null });

const stackStore = new NavigationStackStore(typeof window === 'undefined' ? undefined : window.sessionStorage);

const currentEntryPosition = () => ({
    idx: window.history.state?.idx,
    key: window.history.state?.key,
    pathname: window.location.pathname,
});

/**
 * Tracks the in-app navigation stack so back affordances can return to the page the user came from.
 *
 * The stack is restored from the tab's own sessionStorage, so a reload keeps naming the page back
 * will return to. A newly opened tab has none, so a deep link still reports canGoBack === false and
 * back falls back to a structural parent instead of navigating out of the app.
 */
export function AppHistoryProvider({ children }) {
    const location = useLocation();
    const navigationType = useNavigationType();
    // Restored from the tab's own storage so a reload keeps the trail; a new
    // tab starts empty, which is what preserves the deep-link fallback.
    const [stack, setStack] = useState(() => stackStore.load(currentEntryPosition()));

    useEffect(() => {
        setStack((current) =>
            reduceHistory(current, navigationType, {
                key: location.key,
                // Read at navigation time, not from location: the router writes
                // its index into history.state and it outlives a page load.
                idx: window.history.state?.idx,
                pathname: location.pathname,
                search: location.search,
                label: routeLabels.labelFor(location.pathname),
            }),
        );
    }, [location.key, location.pathname, location.search, navigationType]);

    useEffect(() => {
        stackStore.save(stack);
    }, [stack]);

    const value = useMemo(() => ({ canGoBack: stack.length > 1, previous: previousEntry(stack) }), [stack]);

    return <AppHistoryContext.Provider value={value}>{children}</AppHistoryContext.Provider>;
}

AppHistoryProvider.propTypes = {
    children: PropTypes.node,
};

export const useAppHistory = () => useContext(AppHistoryContext);

export default AppHistoryContext;
